/**
 * The two Premium reminders BabyTrack may leave on the device: the free-trial
 * offer (v1.5.0, docs/v150-spec.md §3.6) and the day-8 second ask (v1.5.1,
 * docs/v151-spec.md §2.6), booked separately since v1.5.3 (docs/v153-spec.md
 * §2.6). Both are LOCAL notifications: a brand-new user has no APNs device
 * row, so a server push would reach nobody.
 *
 *   9961  second ask — booked at the end of set-up (finishSetup), day 8 at
 *         10:00 local: `scheduleSecondAsk({ babyName })`. 7 calendar days
 *         after set-up ends, moved past quiet hours ('defer') — the same day
 *         the server's in-app ask (useSecondAsk) falls due. Opens
 *         /paywall?src=day8-notification&reason=week. Funnel event
 *         `second_ask_scheduled`.
 *   9960  trial offer — one-off, 30 min after the first-day paywall
 *         (src=day1, v1.5.3; the set-up paywall until 1.5.2) is closed without
 *         a purchase: PaywallPage calls `scheduleTrialOffer({ babyName })`.
 *         Moved past quiet hours, opens /offer. Funnel event `offer_scheduled`.
 *
 * Why two calls (docs/v153-spec.md §0 and §5.1): set-up no longer opens the
 * paywall — the first one waits for a day of entries (useFirstAsk). Up to 1.5.2
 * the paywall close booked both notifications together. Now the day-8 one is
 * booked when set-up ends, so a parent who never comes back still gets the one
 * day-8 touch 1.5.2 gave them, and the offer follows only the first-day
 * paywall: nothing follows a paywall the user opened on their own (a gate,
 * Settings, the offer letter).
 *
 * Conditions both calls share (checkConditions), each with the reason returned
 * when it fails, in this order:
 *   native iOS ................................ 'unsupported'
 *   signed in (cached Cognito sub) ............ 'signed-out'
 *   not already booked for this user .......... 'already-scheduled'
 *   prefs.offers === true (explicit consent) .. 'offers-off'
 *   notification permission granted ........... 'no-permission'
 *   a baby was created (its name) ............. 'no-baby'
 *   plan Free ................................. 'not-free'
 *   a trial the user may get .................. 'not-eligible'
 * plus 'error' when the plugin refuses the schedule (or anything throws).
 * Consent is read again once the plugin has booked (withdrawnMeanwhile, the
 * v1.5.3 code review): a sign-out, an account deletion or offers switched off
 * while the booking was in flight cancels what was just booked, and the call
 * resolves 'already-scheduled' (the record now dismissed) or 'offers-off',
 * with nothing tracked or recorded.
 * "Already booked" is each call's own once-per-user rule, read from the state:
 *   scheduleSecondAsk  secondAskAttempted — 'secondAskFireAt' in state, or dismissed
 *   scheduleTrialOffer offerAttempted     — 'fireAt' in state, or dismissed
 * A state marked dismissed (a sign-out, an account deletion or offers switched
 * off in between) means neither is ever booked again: one offer per user, ever.
 * A record that is there but cannot be read counts as booked (fails closed, as
 * up to 1.5.2, when any record at all did).
 *
 * State lives in localStorage under `bt_trial_offer:<sub>` — ONE record for
 * both notifications:
 *   { secondAskScheduledAt, secondAskFireAt,   ← scheduleSecondAsk (set-up)
 *     scheduledAt, fireAt,                     ← scheduleTrialOffer (first-day paywall)
 *     dismissed, trialDays, clearedOnPaid }
 * Each call MERGES its own fields onto whatever is already there (read again
 * just before the write) and never replaces it; the record is never deleted.
 * `secondAskFireAt` is null when the plugin refused 9961 — the attempt still
 * counts, so set-up never books it twice. A failed 9960 records nothing. A
 * record written by 1.5.1/1.5.2 holds both fire times, so neither call books
 * anything more for it. Cancelling (sign-out, account deletion, the Settings
 * toggle, a purchase) removes both notifications and marks the state
 * dismissed. Dismissing the dashboard notice leaves 9961 pending.
 *
 * The first time the plan is seen paid with an offer state on record —
 * dismissed or not, fired or not — useTrialOfferLifecycle cancels both
 * notifications and clears copies still in Notification Centre, then records
 * `clearedOnPaid` (ISO) so it does not repeat on every launch; a cancel that
 * timed out is retried on the next launch (docs/v151-spec.md §6.2).
 *
 * From the 9960 fire time the dashboard notice slot shows "Your N-day free
 * trial is waiting" for 14 days (useTrialOfferNotice), while the plan is Free.
 * A record with no `fireAt` (9961 booked at set-up, 9960 not yet) never shows
 * the notice.
 *
 * useTrialOfferLifecycle also switches Premium's nap reminders on once per account
 * (see applyNapRemindersDefault).
 */

import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { silentFetch } from '../../shared/api/client';
import { getProducts } from '../../shared/services/appleIAP';
import { track } from '../../shared/services/funnel';
import { getCachedAuthUser, withTimeout } from '../../services/network';
import { isIOS, isNative } from '../../shared/utils/platform';
import { useSubscription } from '../subscription/useSubscription';
import { readJourney } from '../onboarding/onboardingStore';
import {
  TRIAL_OFFER_FALLBACK_DAYS,
  cancelSecondAskNotification,
  cancelTrialOfferNotification,
  checkPermission,
  getPrefs,
  savePrefs,
  scheduleSecondAskNotification,
  scheduleTrialOfferNotification,
  secondAskFireTime,
  trialOfferFireTime,
} from '../../services/notifications';

export const TRIAL_OFFER_KEY_PREFIX = 'bt_trial_offer:';
export const TRIAL_OFFER_NOTICE_DAYS = 14;
export const NAP_REMINDERS_DEFAULT_KEY_PREFIX = 'bt_nap_reminders_default:';

const DAY_MS = 24 * 60 * 60 * 1000;
const CANCEL_TIMEOUT_MS = 2000;
const MAX_TIMEOUT_MS = 2 ** 31 - 1; // setTimeout's ceiling (~24.8 days)

// --- State (localStorage, per Cognito sub) ---------------------------------

function currentSub() {
  return getCachedAuthUser()?.sub || null;
}

function readRaw(sub) {
  if (!sub) return null;
  try {
    return localStorage.getItem(`${TRIAL_OFFER_KEY_PREFIX}${sub}`);
  } catch {
    return null;
  }
}

function parseState(raw) {
  if (!raw) return null;
  try {
    const state = JSON.parse(raw);
    return state && typeof state === 'object' ? state : null;
  } catch {
    return null;
  }
}

const listeners = new Set();

function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function writeState(sub, state) {
  if (!sub) return;
  try {
    localStorage.setItem(`${TRIAL_OFFER_KEY_PREFIX}${sub}`, JSON.stringify(state));
  } catch {
    /* storage unavailable — the notice simply won't show */
  }
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {
      /* one bad listener must not break the rest */
    }
  });
}

/** The current user's offer state, or null when none was ever scheduled. */
export function readTrialOfferState(sub = currentSub()) {
  return parseState(readRaw(sub));
}

const toMs = (value) => (typeof value === 'number' ? value : Date.parse(value || ''));

// --- Trial length -----------------------------------------------------------

/**
 * The yearly plan's free-trial length as StoreKit reports it right now:
 * a number of days; null when StoreKit lists the plan without a free trial;
 * undefined when StoreKit has not answered (or this build does not report a
 * trial length) — callers then quote the static 7-day fallback.
 */
export function liveTrialDays() {
  try {
    const yearly = (getProducts() || []).find(
      (p) => p && (p.billingPeriod === 'P1Y' || /yearly/i.test(String(p.id || '')))
    );
    if (!yearly || !yearly.price || !('trialDays' in yearly)) return undefined;
    const days = Number(yearly.trialDays);
    return Number.isInteger(days) && days > 0 ? days : null;
  } catch {
    return undefined;
  }
}

// --- Scheduling -------------------------------------------------------------

/** 9961 booked (or tried) for this user — or every offer withdrawn. */
function secondAskAttempted(state) {
  return !!state && ('secondAskFireAt' in state || state.dismissed === true);
}

/** 9960 scheduled for this user — or every offer withdrawn. */
function offerAttempted(state) {
  return !!state && ('fireAt' in state || state.dismissed === true);
}

const refuse = (reason) => ({ refused: { scheduled: false, reason } });

/**
 * A withdrawal that landed while a booking was in flight (the v1.5.3 code
 * review, 2026-11-04). checkConditions reads the record and prefs.offers once,
 * then awaits the permission check, the status fetch (up to 12 s) and the
 * plugin; cancelTrialOffer — a sign-out, an account deletion, offers switched
 * off in Settings — can run in that window. It finds nothing booked yet to
 * cancel, so without this the notification would outlive the withdrawal, and
 * the offer's write (`dismissed: false`) would re-arm a record it had just
 * marked dismissed. Read both again once the plugin has booked; null when
 * consent still stands. The order is checkConditions': a record now dismissed
 * → 'already-scheduled', then consent off → 'offers-off'.
 */
function withdrawnMeanwhile(sub) {
  if (readTrialOfferState(sub)?.dismissed === true) return 'already-scheduled';
  if (getPrefs().offers !== true) return 'offers-off';
  return null;
}

/** Take back what was just booked: awaited, bounded like cancelTrialOffer, never throws. */
async function cancelJustBooked(cancelNotification) {
  try {
    await withTimeout(cancelNotification(), CANCEL_TIMEOUT_MS);
  } catch {
    /* best effort — useTrialOfferLifecycle and the next cancel still clear it */
  }
}

/**
 * The checks both notifications share, in the header's order, with
 * `attempted(state)` as the caller's once-per-user rule. Resolves
 * { refused: { scheduled: false, reason } } or { sub, name, trialDays }.
 */
async function checkConditions({ babyName, plan, trialEligible } = {}, attempted) {
  if (!isNative() || !isIOS()) return refuse('unsupported');
  const sub = currentSub();
  if (!sub) return refuse('signed-out');
  // Fails closed: a record that is there but cannot be read still says
  // something was booked for this user (up to 1.5.2 any record at all did).
  const raw = readRaw(sub);
  const state = parseState(raw);
  if ((raw && !state) || attempted(state)) return refuse('already-scheduled');
  if (getPrefs().offers !== true) return refuse('offers-off');
  if (!(await checkPermission())) return refuse('no-permission');
  const name = String(babyName || '').trim();
  if (!name) return refuse('no-baby');

  // Ask the server rather than trust a cache; a caller may pass the plan it
  // already holds. When the status cannot be fetched the notification is
  // still booked, as up to 1.5.2: set-up has just ended on a new account and
  // the first-day paywall only opens for a Free one, and
  // useTrialOfferLifecycle cancels both notifications if the plan turns out to
  // be paid.
  let status = { plan, trialEligible };
  if (plan === undefined) status = (await silentFetch('/subscription/status', 'GET')) || {};
  if (status.plan && status.plan !== 'free') return refuse('not-free');
  // Trial wording only for an account that may get the trial: the server says
  // false when it has Apple purchase history, StoreKit null when the yearly
  // plan carries no free trial at all.
  const live = liveTrialDays();
  if (status.trialEligible === false || live === null) return refuse('not-eligible');
  const trialDays = Number.isInteger(live) ? live : TRIAL_OFFER_FALLBACK_DAYS;
  return { sub, name, trialDays };
}

async function runSecondAsk(options) {
  const checked = await checkConditions(options, secondAskAttempted);
  if (checked.refused) return checked.refused;
  const { sub, name, trialDays } = checked;

  // Day 8 counted from the end of set-up, at 10:00 local, quiet hours deferred.
  const fireAt = secondAskFireTime(new Date(), getPrefs());
  const result = await scheduleSecondAskNotification({ babyName: name, trialDays, fireAt });

  // Consent withdrawn while this was in flight (withdrawnMeanwhile): cancel
  // 9961 again, track nothing and write nothing — a dismissed record stays
  // exactly as cancelTrialOffer left it.
  if (result.scheduled) {
    const withdrawn = withdrawnMeanwhile(sub);
    if (withdrawn) {
      await cancelJustBooked(cancelSecondAskNotification);
      return { scheduled: false, reason: withdrawn };
    }
  }

  // Recorded either way (null when the plugin refused): set-up books it once.
  const current = readTrialOfferState(sub);
  writeState(sub, {
    ...current,
    dismissed: current?.dismissed ?? false,
    trialDays,
    secondAskScheduledAt: new Date().toISOString(),
    secondAskFireAt: result.scheduled ? fireAt.toISOString() : null,
  });
  if (!result.scheduled) return result;
  track('second_ask_scheduled');
  return { scheduled: true, fireAt: fireAt.toISOString() };
}

async function runTrialOffer(options) {
  const checked = await checkConditions(options, offerAttempted);
  if (checked.refused) return checked.refused;
  const { sub, name, trialDays } = checked;

  const fireAt = trialOfferFireTime(new Date(), getPrefs());
  const result = await scheduleTrialOfferNotification({ babyName: name, trialDays, fireAt });
  if (!result.scheduled) return result; // nothing recorded

  // Consent withdrawn while this was in flight (withdrawnMeanwhile): cancel
  // 9960 again, track nothing, and never write `dismissed: false` over the
  // withdrawal.
  const withdrawn = withdrawnMeanwhile(sub);
  if (withdrawn) {
    await cancelJustBooked(cancelTrialOfferNotification);
    return { scheduled: false, reason: withdrawn };
  }

  // Merged onto the record set-up left (secondAskScheduledAt, secondAskFireAt).
  const current = readTrialOfferState(sub);
  writeState(sub, {
    ...current,
    dismissed: false,
    trialDays,
    scheduledAt: new Date().toISOString(),
    fireAt: fireAt.toISOString(),
  });
  track('offer_scheduled');
  return { scheduled: true, fireAt: fireAt.toISOString() };
}

let schedulingSecondAsk = null; // the in-flight call, shared — booked once
let schedulingOffer = null; // the in-flight call, shared — a double close schedules once

/**
 * Book the day-8 second ask (9961) — called by finishSetup when set-up ends
 * (docs/v153-spec.md §2.1). Once per user. Resolves { scheduled: true, fireAt }
 * or { scheduled: false, reason } (reasons in the header). Never rejects.
 * Optional `plan` / `trialEligible` skip the status fetch.
 */
export function scheduleSecondAsk(options = {}) {
  if (!schedulingSecondAsk) {
    schedulingSecondAsk = runSecondAsk(options)
      .catch(() => ({ scheduled: false, reason: 'error' }))
      .finally(() => {
        schedulingSecondAsk = null;
      });
  }
  return schedulingSecondAsk;
}

/**
 * Schedule the one-off trial offer (9960) — called when the first-day paywall
 * (src=day1) is closed without a purchase. It never books 9961 (set-up did).
 * Once per user. Resolves { scheduled: true, fireAt } or
 * { scheduled: false, reason } (reasons in the header). Never rejects.
 * Optional `plan` / `trialEligible` (from useSubscription) skip the status
 * fetch.
 */
export function scheduleTrialOffer(options = {}) {
  if (!schedulingOffer) {
    schedulingOffer = runTrialOffer(options)
      .catch(() => ({ scheduled: false, reason: 'error' }))
      .finally(() => {
        schedulingOffer = null;
      });
  }
  return schedulingOffer;
}

/**
 * Cancel the offer: remove the pending (or delivered) notifications — the
 * 9960 offer and the 9961 second ask — and mark this user's offer dismissed so
 * the dashboard notice never shows. Called by sign-out and account deletion
 * (BEFORE Auth.signOut, while the user is still known), the Settings offers
 * toggle and useTrialOfferLifecycle. Never rejects and never hangs a sign-out:
 * resolves true once the plugin has answered both cancels (on the web there is
 * nothing to cancel), false when it did not answer within CANCEL_TIMEOUT_MS.
 */
export async function cancelTrialOffer() {
  try {
    const sub = currentSub();
    const state = readTrialOfferState(sub);
    if (sub && state && !state.dismissed) writeState(sub, { ...state, dismissed: true });
    await withTimeout(
      Promise.all([cancelTrialOfferNotification(), cancelSecondAskNotification()]),
      CANCEL_TIMEOUT_MS
    );
    return true;
  } catch {
    /* best effort */
    return false;
  }
}

// --- Hooks --------------------------------------------------------------------

/** Re-renders at `startMs` and again at `endMs`, so the notice appears and expires on time. */
function useWindowClock(startMs, endMs) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!Number.isFinite(startMs)) return undefined;
    const current = Date.now();
    let next = null;
    if (current < startMs) next = startMs;
    else if (current < endMs) next = endMs;
    if (next === null) return undefined;
    const timer = setTimeout(() => setNow(Date.now()), Math.min(next - current + 500, MAX_TIMEOUT_MS));
    return () => clearTimeout(timer);
  }, [startMs, endMs, now]);
  return now;
}

/**
 * The dashboard notice: visible from the fire time until 14 days later, while
 * the plan is Free, the offer has not been dismissed and a trial is on offer.
 * Returns { visible, dismiss, trialDays }.
 */
export function useTrialOfferNotice() {
  const [sub] = useState(currentSub);
  const raw = useSyncExternalStore(
    subscribe,
    () => readRaw(sub),
    () => null
  );
  const state = useMemo(() => parseState(raw), [raw]);
  const { plan, isLoading, trialEligible, hasStatus } = useSubscription();

  const fireAtMs = toMs(state?.fireAt);
  const endMs = fireAtMs + TRIAL_OFFER_NOTICE_DAYS * DAY_MS;
  const now = useWindowClock(fireAtMs, endMs);

  const live = liveTrialDays();
  const stored = Number(state?.trialDays);
  const trialDays = Number.isInteger(live)
    ? live
    : Number.isInteger(stored) && stored > 0
      ? stored
      : TRIAL_OFFER_FALLBACK_DAYS;

  const visible =
    !!state &&
    !state.dismissed &&
    Number.isFinite(fireAtMs) &&
    now >= fireAtMs &&
    now < endMs &&
    !isLoading &&
    // Fails closed (docs/v151-spec.md §6.2): no trial notice until the server
    // has answered — a failed or offline-paused status is not "eligible".
    hasStatus === true &&
    plan === 'free' &&
    trialEligible !== false &&
    live !== null;

  const dismiss = useCallback(() => {
    const current = readTrialOfferState(sub);
    if (sub && current && !current.dismissed) writeState(sub, { ...current, dismissed: true });
  }, [sub]);

  return { visible, dismiss, trialDays };
}

// --- Premium nap-reminder default ---------------------------------------------

const napDefaultInMemory = new Set(); // storage refused: decided for this session
let napDefaultRunning = false; // StrictMode runs the effect twice — decide once

function napDefaultDecided(sub) {
  if (napDefaultInMemory.has(sub)) return true;
  try {
    return !!localStorage.getItem(`${NAP_REMINDERS_DEFAULT_KEY_PREFIX}${sub}`);
  } catch {
    return false;
  }
}

function markNapDefaultDecided(sub) {
  try {
    localStorage.setItem(`${NAP_REMINDERS_DEFAULT_KEY_PREFIX}${sub}`, new Date().toISOString());
  } catch {
    napDefaultInMemory.add(sub);
  }
}

/**
 * Nap and bedtime reminders — and with them the lock-screen Live Activity — are a
 * Premium setting that stays off until switched on (prefs.napReminders). Up to 1.4.3 a
 * new account was inside its 7-day preview when the post-baby prompt ran, and the prompt
 * switched them on. A 1.5.0 account is on Free at that point, so the onboarding's
 * "Allow notifications" leaves them off, and nothing on the way to Premium (a purchase,
 * the yearly trial, a pass) switched them on.
 *
 * So, once per account on this device: the first time an account whose first-run
 * journey happened here (onboardingStore.readJourney) is seen on a paid plan, nap
 * reminders are switched on — only when notification permission is already granted
 * (checked silently, never prompted). The decision is recorded either way
 * ('bt_nap_reminders_default:<sub>'), so a later "off" in Settings is respected, and an
 * account that predates the journey (an existing subscriber updating) keeps its
 * settings exactly as they are. Never rejects.
 */
export async function applyNapRemindersDefault(sub = currentSub()) {
  if (!sub || napDefaultRunning || napDefaultDecided(sub) || !readJourney(sub)) return;
  napDefaultRunning = true;
  try {
    const granted = await checkPermission();
    if (granted) {
      const prefs = getPrefs();
      if (prefs.napReminders !== true) savePrefs({ ...prefs, napReminders: true });
    }
    markNapDefaultDecided(sub);
  } catch {
    /* best effort — the Settings toggle still works */
  } finally {
    napDefaultRunning = false;
  }
}

/**
 * Mounted once in Layout: when the plan turns paid (a purchase, a restore, a
 * comp or a pass) the offer is cleared — a new subscriber must never be told
 * their free trial is waiting, nor asked again on day 8 — and Premium's nap
 * reminders get their once-per-account default (applyNapRemindersDefault).
 *
 * Clearing (docs/v151-spec.md §6.2): whenever an offer state exists, dismissed
 * or not, fire times past or not, cancelTrialOffer() cancels both
 * notifications and removes their delivered copies, then `clearedOnPaid` is
 * recorded so later launches leave it alone. A cancel that timed out records
 * nothing, so the next launch tries again.
 */
export function useTrialOfferLifecycle() {
  const { plan, isLoading } = useSubscription();
  const paid = !isLoading && !!plan && plan !== 'free';

  useEffect(() => {
    if (!paid) return;
    const sub = currentSub();
    const state = readTrialOfferState(sub);
    if (!state || state.clearedOnPaid) return;
    cancelTrialOffer().then((cleared) => {
      // Re-read: cancelTrialOffer has just marked the state dismissed.
      const current = readTrialOfferState(sub);
      if (cleared && current) writeState(sub, { ...current, clearedOnPaid: new Date().toISOString() });
    });
  }, [paid]);

  useEffect(() => {
    if (paid) applyNapRemindersDefault();
  }, [paid]);
}
